import { useCallback, useEffect, useRef, useState } from 'react';

const ITEMS_BATCH_NUMBER = 20;

const getFixedNumberItems = (allItems, numberToCutAt) => {
  if (!allItems || !Array.isArray(allItems)) {
    return [];
  }

  return allItems.slice(0, numberToCutAt);
};

/**
 * Hook for ChapterListItem that 'paginates' list items.
 * Used with a "Load More" button when list length degrades Reader performance.
 * Currently only used in the Notes & Highlights learning tools menu.
 *
 * @param {Array} items - list of react elements
 * @returns {Object} - object with a (smaller) list of items, a function to load more items, and a boolean for whether there are more items available to load.
 */
export const useLimitedItems = (items) => {
  const lastItemsCountRef = useRef(0);
  const [limitedItems, setLimitedItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  const handleLimitedItemStateChange = useCallback((items, number) => {
    const newItems = getFixedNumberItems(items, number);
    setLimitedItems(newItems);
    setHasMore(items?.length > newItems.length);
  }, []);

  useEffect(
    function updateStateWhenItemsChange() {
      handleLimitedItemStateChange(
        items,
        lastItemsCountRef.current + ITEMS_BATCH_NUMBER,
      );
    },
    [handleLimitedItemStateChange, items],
  );

  useEffect(
    function focusLastCard() {
      if (limitedItems.length > ITEMS_BATCH_NUMBER) {
        const cardIdentifier = limitedItems[lastItemsCountRef.current].key;

        document.querySelector(`[id='${cardIdentifier}'] button`)?.focus();
      }
    },
    [limitedItems],
  );

  /**
   * Callback function to add more items to `limitedItems` state.
   * onClick action for a "Load More" button.
   */
  const handleLoadMore = useCallback(() => {
    lastItemsCountRef.current = limitedItems.length;
    handleLimitedItemStateChange(
      items,
      limitedItems.length + ITEMS_BATCH_NUMBER,
    );
  }, [handleLimitedItemStateChange, items, limitedItems]);

  return { limitedItems, handleLoadMore, hasMore };
};
